import React, { useEffect, useState } from "react";
import { useKeycloakToken } from '../common/useKeycloak';
import AddConnectionDetails from "./AddConnectionDetails";
import { useNavigate, useParams, useLocation } from 'react-router-dom';
import { Connection, buildDestination, Destination, DestinationType, NewIntegration, Organization, ProviderType, buildProviderType, buildNewIntegration, ConnectionConfiguration } from "../common/ObjectTypes";
import { addConnectionDestinations, createNewConnection, getConnectionDetails } from "../../api/connections/connectionsApi";
import Spinner from "../common/Spinner";
import { getDestinationDetails, getDestinationsList } from "../../api/destinations/destinationsApi";
import { toast } from "react-toastify";
import Ajv from 'ajv';
import addFormats from 'ajv-formats';
import { loadOrganizations } from "../../api/organizations/loadOrganizations";
import { filter_page_size, page_size } from "../../api/apiUtils";
import { loadDestinationTypes } from '../../api/destinations/loadDestinationTypes';
import { loadProviderTypes } from '../../api/connections/loadProviderTypesNewConnection';
import { useTranslation } from "react-i18next";
import { ManageDestinationDetails } from "../destinations/ManageDestinationDetails";
import NewConnectionStepThree from "./NewConnectionStepThree";
import { useKeycloak } from "@react-keycloak/web";
import ConnectionProgressIndicator from "./NewConnectionProgressIndicator/ConnectionProgressIndicator";
import SettingUpModal from "./NewConnectionProgressIndicator/SettingUpModal";
import { containerClasses, buttonClasses, textClasses } from "../common/tailwindClassnames";
import AddDestinationChoiceView from "../destinations/AddDestinationChoiceView";
import { trackDestinationAdded } from "../../analytics/analytics";
import { getConnectionKind } from "../common/connectionKind";

function useScrollLock(lock: boolean) {
    useEffect(() => {
        if (lock) {
            const original = document.body.style.overflow;
            document.body.style.overflow = 'hidden';
            return () => { document.body.style.overflow = original; };
        }
    }, [lock]);
}

export function ManageAddConnectionStepThree() {
    type FilterTypes = {
        endpoint__in: FilterTags[],
        enabled: FilterTags[],
        type__in: FilterTags[],
        owner__in: FilterTags[],
        ordering: string,
        search: string
    };
    function createFilterTypes(
        endpoint__in: FilterTags[],
        enabled: FilterTags[],
        type__in: FilterTags[],
        owner__in: FilterTags[],
        ordering: string,
        search: string
    ): FilterTypes {
        return {
            endpoint__in: endpoint__in || [],
            enabled: enabled || [],
            type__in: type__in || [],
            owner__in: owner__in || [],
            ordering: ordering || "",
            search: search || ""
        }
    }
    type FilterTags = {
        name: string,
        id: string
    };
    const { t } = useTranslation();
    const { connection_id } = useParams();
    const location = useLocation();
    const [token, showLoginModal, setShowLoginModal] = useKeycloakToken();
    const { keycloak } = useKeycloak();
    const [destinationFilter, setDestinationFilter] = useState<FilterTypes>(() => {
        const savedFilter = sessionStorage.getItem('newConnectionDestinationFilter');
        return savedFilter ? JSON.parse(savedFilter) : createFilterTypes([], [], [], [], "", "")
    });
    const [initialConnection, setInitialConnection] = useState<NewIntegration>(buildNewIntegration(true))
    const [createdConnection, setCreatedConnection] = useState<Connection>()
    const [errors, setErrors] = useState({});
    const [rsjfErrors, setRsjfErrors] = useState<any[]>([]);
    const [routeId, setRouteId] = useState("");
    const [showSettingUpModal, setShowSettingUpModal] = useState(false);
    // Scroll to top and lock scroll when modal is shown
    useEffect(() => {
        if (showSettingUpModal) {
            window.scrollTo({ top: 0, behavior: 'smooth' });
        }
    }, [showSettingUpModal]);
    useScrollLock(showSettingUpModal);

    /* 
        CONNECTION DESTINATIONS:
            - "destinations" array is for destinations already associated with the connection
            - "existingDestPhase0" is for existing destinations that are available to add and unselected
            - "existingDestPhase1" is for existing destinations that have been checked and selected to add (yet to be sorted)
            - "existingDestPhase2" is for selected destinations that are sorted to the top of the available list so they remain there
            regardless of search/filter results
            - "existingDestPhase3" is for unselected destination that are still in their sorted position at the top of the available list
    */
    const [destinations, setDestinations] = useState<Destination[]>([]);
    const [existingDestPhase0, setExistingDestPhase0] = useState<Destination[]>(() => {
        const savedDestinations = sessionStorage.getItem('existingDestPhase0');
        return savedDestinations ? JSON.parse(savedDestinations) : []
    });
    const [existingDestPhase1, setExistingDestPhase1] = useState<Destination[]>([]);
    const [existingDestPhase2, setExistingDestPhase2] = useState<Destination[]>([]);
    const [existingDestPhase3, setExistingDestPhase3] = useState<Destination[]>([]);
    const [reloadDestinations, setReloadDestinations] = useState(true);

    const [providerTypes, setProviderTypes] = useState<ProviderType[]>([]);
    const [reloadProviderTypes, setReloadProviderTypes] = useState(true);
    const [destinationTypes, setDestinationTypes] = useState<DestinationType[]>([]);
    const [loadingDestinationTypes, setLoadingDestinationTypes] = useState(false);
    const [organizations, setOrganizations] = useState<Organization[]>([]);
    const [loadingOrganizations, setLoadingOrganizations] = useState(false);
    const [destDetailsModal, setDestDetailsModal] = useState<{ isOpen: boolean; destId: string | null }>({
        isOpen: false,
        destId: null,
    });

    const [selectedProviderType, setSelectedProviderType] = useState<ProviderType>(buildProviderType());
    const [destinationOffset, setDestinationOffset] = useState<number>(0);
    const [isProviderTypeSelected, setIsProviderTypeSelected] = useState(false);
    const [pageLoaded, setPageLoaded] = useState(false);
    const [currentStep, setCurrentStep] = useState(3);
    const navigate = useNavigate();
    // Remove addDestModal state, not needed for page navigation
    const [addExistingDest, setAddExistingDest] = useState(false);
    const [showChoiceView, setShowChoiceView] = useState(!(location.state as any)?.skipChoiceView);
    const [updatingDestinations, setUpdatingDestinations] = useState(false);

    const loadConnectionDetails = async () => {

        if (token && typeof token === 'string' && !keycloak.isTokenExpired() && connection_id) {
            try {
                const tempConnection = await getConnectionDetails(token, connection_id);
                setRouteId(tempConnection.default_route.id);
                setDestinationFilter((prevFilter: any) => ({
                    ...prevFilter,
                    owner__in: tempConnection.owner ? [{ name: tempConnection.owner.name, id: tempConnection.owner.id }] : [],
                }));
                setCreatedConnection(tempConnection);
                const loadedDestinations = Array.isArray(tempConnection.destinations) ? tempConnection.destinations : [];
                setDestinations(loadedDestinations);
                if (loadedDestinations.length > 0) {
                    setShowChoiceView(false);
                }
                setPageLoaded(true);
            } catch (err) {
                console.log(err);
            }
        }
    }

    const loadOrganizationsData = async () => {
        if (token && typeof token === 'string' && !keycloak.isTokenExpired()) {
            setLoadingOrganizations(true);
            try {
                let accumulated: Organization[] = [];
                let orgsOffset = 0;
                while (true) {
                    const [orgs, total, error] = await loadOrganizations(token, orgsOffset, "", accumulated);
                    if (error) break;
                    accumulated = orgs;
                    if (orgsOffset + filter_page_size >= total) break;
                    orgsOffset += filter_page_size;
                }
                setOrganizations(accumulated);
            } finally {
                setLoadingOrganizations(false);
            }
        }
    }

    const loadDestinationTypesData = async () => {
        if (token && typeof token === 'string' && !keycloak.isTokenExpired()) {
            setLoadingDestinationTypes(true);
            try {
                let accumulated: DestinationType[] = [];
                let typesOffset = 0;
                while (true) {
                    const [types, total, error] = await loadDestinationTypes(token, typesOffset, "", accumulated);
                    if (error) break;
                    accumulated = types;
                    if (typesOffset + filter_page_size >= total) break;
                    typesOffset += filter_page_size;
                }
                setDestinationTypes(accumulated);
            } finally {
                setLoadingDestinationTypes(false);
            }
        }
    }
    const loadProviderTypesData = async (search_str: string) => {
        if (token && typeof token === 'string' && !keycloak.isTokenExpired()) {
            setReloadProviderTypes(true);
            const [provider_types, error] = await loadProviderTypes(token, search_str);
            if (!error) {
                setProviderTypes(provider_types);
                setReloadProviderTypes(false);
            }
            setPageLoaded(true);
        }
    }

    useEffect(() => {
        sessionStorage.setItem('newConnectionDestinationFilter', JSON.stringify(destinationFilter));
        const loadDestinations = async (destArray: Destination[]) => {
            if (token && typeof token === 'string' && !keycloak.isTokenExpired()) {
                setExistingDestPhase2(existingDestPhase2.concat(existingDestPhase1));
                setExistingDestPhase3(existingDestPhase2.concat(existingDestPhase1));
                sessionStorage.setItem("existingDestPhase2", JSON.stringify(existingDestPhase2.concat(existingDestPhase1)));
                sessionStorage.setItem("existingDestPhase3", JSON.stringify(existingDestPhase2.concat(existingDestPhase1)));
                setUpdatingDestinations(true);
                try {
                    const destinationFilterQuery = {
                        endpoint__in: destinationFilter.endpoint__in.map(a => a.id).toString(),
                        enabled: destinationFilter.enabled.map(a => a.id).toString(),
                        type__in: destinationFilter.type__in.map(a => a.id).toString(),
                        owner__in: destinationFilter.owner__in.map(a => a.id).toString(),
                        ordering: destinationFilter.ordering,
                        search: destinationFilter.search,
                        offset: destinationOffset,
                        previous: process.env.REACT_APP_BASE_URL_LOCAL + "/integrations/?pager=limit&limit=" + page_size + "&offset=" + (destinationOffset - page_size),
                        next: process.env.REACT_APP_BASE_URL_LOCAL + "/integrations/?pager=limit&limit=" + page_size + "&offset=" + (destinationOffset + page_size)
                    }
                    const temp = await getDestinationsList(token, destinationFilterQuery);
                    // check to see if destination in temp.result is in the selected array
                    const tempDestArray: Destination[] = temp.results.filter((b: Destination) => !destArray.map((a: Destination) => a.id).includes(b.id))

                    setExistingDestPhase0(tempDestArray);
                    sessionStorage.setItem("existingDestPhase0", JSON.stringify(tempDestArray));
                    sessionStorage.setItem("existingDestPhase1", JSON.stringify([]));
                    setExistingDestPhase1([]);
                    setUpdatingDestinations(false);
                    setReloadDestinations(false);
                } catch (err) {
                    console.log(err)
                }
            }
        }


        if (reloadProviderTypes) loadProviderTypesData("");

        if (reloadDestinations) loadDestinations([...destinations, ...existingDestPhase1, ...existingDestPhase2]);
        loadOrganizationsData();
        loadDestinationTypesData();
        if (!pageLoaded) loadConnectionDetails();
    }, [token, reloadDestinations, providerTypes])


    const handleClickAddDestinationsStep = () => {
        const providerType = createdConnection?.provider?.type;
        const kind = providerType ? getConnectionKind(providerType) : 'standard';
        let targetTab: string | null;
        switch (kind) {
            case 'webhook': targetTab = 'webhook'; break;
            case 'push-only': targetTab = 'provider'; break;
            default: targetTab = null;
        }
        if (targetTab) {
            handleAddSelectedExistingDestinations(targetTab);
        } else {
            setShowSettingUpModal(true);
        }
    }
    const handleCloseModalAndAddDestinations = () => {
        setShowSettingUpModal(false);
        handleAddSelectedExistingDestinations();
    }
    const handleCloseModalAndStayOnPage = () => {
        setShowSettingUpModal(false);
    }

    const handleToggleDestinationDetailsModal = async (destId?: string) => {
        if (!destId) {
            // Close modal and remove destination details from URL
            setDestDetailsModal({ isOpen: false, destId: null });
            navigate(`/connections/new/${connection_id}/configuredestinations`);
            return;
        }

        // Open modal and update URL
        navigate(`/connections/new/${connection_id}/configuredestinations/destination/${destId}/configuration`);
        if (!destDetailsModal.isOpen || destDetailsModal.destId !== destId) {
            setDestDetailsModal({ isOpen: true, destId: destId });
        }
    };

    const handleSelectExistingDestination = (dest: Destination) => {
        let tempPhase0Array: Destination[] = [...existingDestPhase0];
        let tempPhase1Array: Destination[] = [...existingDestPhase1];
        let tempPhase2Array: Destination[] = [...existingDestPhase2];
        let tempPhase3Array: Destination[] = [...existingDestPhase3];

        const indexInPhase0 = tempPhase0Array.findIndex((a: Destination) => a.id === dest.id);
        const indexInPhase1 = tempPhase1Array.findIndex((a: Destination) => a.id === dest.id);
        const indexInPhase2 = tempPhase2Array.findIndex((a: Destination) => a.id === dest.id);
        const indexInPhase3 = tempPhase3Array.findIndex((a: Destination) => a.id === dest.id);

        if (indexInPhase0 !== -1) {
            if (indexInPhase1 !== -1) {
                // Remove from phase 1
                tempPhase1Array.splice(indexInPhase1, 1);
                sessionStorage.setItem("existingDestPhase1", JSON.stringify(tempPhase1Array));
                setExistingDestPhase1(tempPhase1Array);
            } else {
                // Add to phase 1
                tempPhase1Array.push(dest);
                setExistingDestPhase1(tempPhase1Array);
                sessionStorage.setItem("existingDestPhase1", JSON.stringify(tempPhase1Array));
            }
        }

        if (indexInPhase3 !== -1) {
            if (indexInPhase2 !== -1) {
                // Remove from phase 2
                tempPhase2Array.splice(indexInPhase2, 1);
                setExistingDestPhase2(tempPhase2Array);
                sessionStorage.setItem("existingDestPhase2", JSON.stringify(tempPhase2Array));
            } else {
                // Add to phase 2
                tempPhase2Array.push(dest);
                setExistingDestPhase2(tempPhase2Array);
                sessionStorage.setItem("existingDestPhase2", JSON.stringify(tempPhase2Array));
                // Remove from phase 1
                // Remove from phase 1
                tempPhase1Array.splice(indexInPhase1, 1);
                setExistingDestPhase1(tempPhase1Array);
                sessionStorage.setItem("existingDestPhase1", JSON.stringify(tempPhase1Array));
            }
        }
    }

    const clearExistingDestArrays = () => {
        sessionStorage.removeItem("existingDestPhase0");
        sessionStorage.removeItem("existingDestPhase1");
        sessionStorage.removeItem("existingDestPhase2");
        sessionStorage.removeItem("existingDestPhase3");
        setExistingDestPhase0([]);
        setExistingDestPhase1([]);
        setExistingDestPhase2([]);
        setExistingDestPhase3([]);
    }

    const handleAddSelectedExistingDestinations = async (targetTab?: string) => {
        const destination_ids = {
            "destinations": existingDestPhase1.map(a => a.id).concat(destinations.map(a => a.id), existingDestPhase2.map(a => a.id))
        }
        setUpdatingDestinations(true);
        if (token && typeof token === 'string' && !keycloak.isTokenExpired()) {
            try {
                await addConnectionDestinations(token, routeId, destination_ids);
                existingDestPhase1.concat(existingDestPhase2).forEach((d: Destination) => {
                    trackDestinationAdded(createdConnection?.id, d.type?.name, createdConnection?.owner?.name);
                });
                setDestinations(existingDestPhase1.concat(destinations, existingDestPhase2));
                toast.success(`${t("Connection updated")}`, {
                    position: toast.POSITION.TOP_RIGHT
                });

                sessionStorage.removeItem("newConnectionDestinationFilter");
                navigate(`/connections/${createdConnection?.id}/${targetTab || "destinations"}`, {
                    state: {
                        fromAddConnectionFlow: true
                    }
                });
                clearExistingDestArrays();
            } catch (error) {
                console.log(error)
            }
        }

        setUpdatingDestinations(false);
        setAddExistingDest(false);
    }

    const handleClickAddDestination = (existing: boolean) => {
        if (existing) {
            clearExistingDestArrays()
            setReloadDestinations(true);
            setAddExistingDest(!addExistingDest);
        } else {
            // Navigate to add destination page for this connection
            navigate(`/connections/new/${connection_id}/configuredestinations/newdestination`);
        }
    }

    const handleChangeDestinationTypeFilter = (selected: any) => {
        const tags: FilterTags[] = selected ? [{ name: selected.label, id: selected.value }] : [];
        setDestinationFilter(prev => ({ ...prev, type__in: tags }));
        setReloadDestinations(true);
    }

    const handleChangeDestinationOwnerFilter = (selected: any) => {
        const tags: FilterTags[] = selected ? [{ name: selected.label, id: selected.value }] : [];
        setDestinationFilter(prev => ({ ...prev, owner__in: tags }));
        setReloadDestinations(true);
    }


    // Helper: check if any destination in phase 1, 2, or 3 matches the search term (name or base_url)
    const searchTerm = destinationFilter.search || "";
    const lowerSearch = searchTerm.trim().toLowerCase();
    function matchesSearch(dest: Destination) {
        return (
            dest.name.toLowerCase().includes(lowerSearch) ||
            dest.base_url.toLowerCase().includes(lowerSearch)
        );
    }
    const hasOtherPhaseResultsInSearch =
        !!lowerSearch && ([...existingDestPhase1, ...existingDestPhase2, ...existingDestPhase3].some(matchesSearch));

    const handleGlobalDestinationSearch = (searchSubStr: string) => {
        setDestinationFilter((prevFilter: any) => ({
            ...prevFilter,
            search: searchSubStr
        }));
        setReloadDestinations(true);
    }

    const formIsValid = () => {
        let form_errors: any = {};
        let rsjf_errors: any = {};
        if (initialConnection.name.length === 0) form_errors.name = "Name is required."
        if (initialConnection.name.length > 50) form_errors.name = "Name cannot exceed 50 characters.";
        if (initialConnection.base_url.length > 0) {
            try {
                new URL(initialConnection.base_url);
            } catch (err) {
                form_errors.base_url = "Enter a valid url including the scheme."
            }
        }
        if (initialConnection.owner === "") form_errors.owner = "Select a workspace."

        setErrors(form_errors)

        setRsjfErrors(rsjf_errors);
        return Object.keys(form_errors).length === 0 && Object.keys(rsjf_errors).length === 0;
    }

    const handleCreateConnection = async (connectionToAdd: NewIntegration) => {
        if (!formIsValid()) return;
        if (token && typeof token === 'string' && !keycloak.isTokenExpired() && formIsValid()) {
            const temp = await createNewConnection(token, connectionToAdd);
            setCreatedConnection(temp)
            setRouteId(temp.default_route.id);
            toast.success(`${t("New connection made, ready to add destinations")}`, {
                position: toast.POSITION.TOP_RIGHT
            });
        }
    }

    const handleCancel = () => {
        setShowSettingUpModal(false);
        const providerType = createdConnection?.provider?.type;
        const kind = providerType ? getConnectionKind(providerType) : 'push-only';
        const targetTab = kind === 'webhook' ? 'webhook' : 'provider';
        navigate(`/connections/${createdConnection?.id}/${targetTab}`, {
            state: {
                fromAddConnectionFlow: true
            }
        });
    }

    const noDestinationsSelected = existingDestPhase1.length === 0 && existingDestPhase2.length === 0;

    const toSingleValue = (tags: FilterTags[]) =>
        tags.length > 0 ? { label: tags[0].name, value: tags[0].id } : null;

    const typeSelectValue = toSingleValue(destinationFilter.type__in);
    const ownerSelectValue = toSingleValue(destinationFilter.owner__in);

    const providerType = createdConnection?.provider?.type;
    const isWebhook = providerType
        ? getConnectionKind(providerType) === 'webhook'
        : !!(location.state as { isWebhook?: boolean })?.isWebhook;

    return !pageLoaded ?
        (
            <Spinner />
        ) : (
            <div>
                {showSettingUpModal && <SettingUpModal
                    selectedProviderType={selectedProviderType.name}
                    onCloseAndAddDestinations={handleCloseModalAndAddDestinations}
                    onCloseAndStayOnPage={handleCloseModalAndStayOnPage}
                />}
                {destDetailsModal.isOpen &&
                    <ManageDestinationDetails />
                }
                <div className="bg-white shadow static inset-x-0 px-12 pt-5 pb-1 -mt-5 -mx-5 desktop:-mx-12 transition-colors duration-300">
                    <div className="max-w-list-table mx-auto">
                        <h1 className="text-3xl font-semibold mb-3 text-off-black transition-colors duration-300">{t("New")} {selectedProviderType.name} {t("Connection")}</h1>
                    </div>
                </div>
                <div className="max-w-list-table mx-auto">
                    <ConnectionProgressIndicator
                        stepZeroStatus="completed"
                        stepOneStatus="completed"
                        stepTwoStatus="in-progress"
                        webhookStepStatus={isWebhook ? "upcoming" : undefined}
                        confirmationStatus="upcoming"
                    />
                    {showChoiceView ? (
                        <AddDestinationChoiceView
                            onAddExisting={() => {
                                clearExistingDestArrays();
                                setReloadDestinations(true);
                                setAddExistingDest(true);
                                setShowChoiceView(false);
                            }}
                            onCreateNew={() => navigate(`/connections/new/${connection_id}/configuredestinations/newdestination`)}
                        />
                    ) : (
                        <NewConnectionStepThree
                            destinations={destinations}
                            existingDestPhase0={existingDestPhase0}
                            existingDestPhase1={existingDestPhase1}
                            existingDestPhase2={existingDestPhase2}
                            existingDestPhase3={existingDestPhase3}
                            onSelectExistingDestination={handleSelectExistingDestination}
                            destinationTypes={destinationTypes}
                            loadingDestinationTypes={loadingDestinationTypes}
                            organizations={organizations}
                            loadingOrganizations={loadingOrganizations}
                            globalSearchStr={destinationFilter.search}
                            onGlobalDestinationFilter={handleGlobalDestinationSearch}
                            onChangeTypeFilter={handleChangeDestinationTypeFilter}
                            typeFilterValue={typeSelectValue}
                            onChangeOwnerFilter={handleChangeDestinationOwnerFilter}
                            ownerFilterValue={ownerSelectValue}
                            addExistingDest={addExistingDest}
                            updatingDestinations={updatingDestinations}
                            onClickAddDestination={handleClickAddDestination}
                            onAddExistingDestinations={handleAddSelectedExistingDestinations}
                            onViewDetails={handleToggleDestinationDetailsModal}
                            searchTerm={searchTerm}
                        />
                    )}
                </div>

                <div className="fixed bottom-0 right-0 py-3 bg-white/10 backdrop-blur w-full shadow-lg transition-colors duration-300">
                    <div className="max-w-list-table mx-auto flex flex-row gap-3 px-8">
                        {!showChoiceView && (
                            <button className={buttonClasses.primary}
                                disabled={noDestinationsSelected}
                                aria-disabled={noDestinationsSelected}
                                onClick={() => handleClickAddDestinationsStep()}
                                data-testid="ManageAddConnectionStepThree-add-destinations-button"
                            >
                                {t("Add Destinations")}
                            </button>
                        )}
                        <button className={buttonClasses.cancel}
                            type="button"
                            onClick={() => handleCancel()}
                        >
                            {t("Skip Step")}
                        </button>
                    </div>
                </div>
            </div>
        )
}